import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MediaRequests } from "@app/shared/src/api/api.gen";
import { getApiUrl } from "@app/shared/src/api/api_client";
import { Device } from "@app/shared/src/models";
import {
    Btn,
    Card,
    CardHead,
    Note,
    Spinner,
    Table,
    Td,
    Th,
    formatDateTime,
    formatTimeOfDay,
    timeZoneLabel,
} from "@app/shared/src/components_v2";
import { isMediaRequestWorking } from "../../helpers/mediaRequestStatus";
import { useLoad } from "../../hooks/useLoad";
import { NewRequestModal } from "../pages/MediaPage";
import { MediaRequestStatusTag } from "../shared/MediaRequestStatusTag";

/** How often a pull in progress re-reads itself. A clip off the DVR lands in a couple of minutes. */
const CLIP_POLL_MS = 15_000;

/**
 * How many pulls the card shows. It is also how many the server is asked for: the unpaged list
 * endpoint returns every request a unit has ever had, which on a busy sensor is hundreds of
 * thousands of rows and more than the shared client's 30 s timeout allows.
 */
const CARD_ROWS = 8;

/**
 * This unit's DVR pulls, with the two things a director does from here: ask for more footage, or
 * jump to the fleet-wide list filtered to this truck.
 */
export const DeviceMediaSection: React.FC<{ device: Device }> = ({ device }) => {
    // Nothing pushes here: while the unit is recording, encoding or uploading a clip the list
    // re-reads itself so the stage moves, and goes quiet again once every pull has settled.
    const [pollMs, setPollMs] = useState<number>();
    const clips = useLoad(
        () => MediaRequests.getRequestsInfiniteScroll(device.id, CARD_ROWS).then((page) => page?.data ?? []),
        [device.id],
        { pollMs },
    );
    const [requestOpen, setRequestOpen] = useState(false);

    // Newest first, sorted here as well as on the server so the order doesn't depend on which
    // API version answered.
    const rows = [...(clips.data ?? [])]
        .sort((a, b) => new Date(b.requestDate ?? 0).getTime() - new Date(a.requestDate ?? 0).getTime())
        .slice(0, CARD_ROWS);

    // Only what the card shows decides whether to keep polling. Judging every request the unit
    // has ever had would keep the card polling forever on a unit with one old pull stuck
    // mid-upload — and there are units like that.
    const working = rows.some(isMediaRequestWorking);
    useEffect(() => setPollMs(working ? CLIP_POLL_MS : undefined), [working]);
    const viewUrl = (requestId?: string) => getApiUrl(`/api/mediarequest/${device.id}/media-requests/${requestId}/video/view`);

    return (
        <Card>
            <CardHead title="DVR requests" note="video pulled from this unit">
                <Link to={`/media?device=${device.id}`}>
                    <Btn tone="quiet">View all</Btn>
                </Link>
                <Btn onClick={() => setRequestOpen(true)}>Request a clip</Btn>
            </CardHead>

            {clips.error && (
                <div className="px-5 pt-3">
                    <Note tone="crit">{clips.error}</Note>
                </div>
            )}

            {clips.isLoading ? (
                <div className="grid place-items-center p-8 text-[var(--muted)]">
                    <Spinner />
                </div>
            ) : clips.error ? null : rows.length === 0 ? (
                <p className="px-5 pb-5 pt-3 text-[13px] text-[var(--muted)]">No clips have been pulled from this unit.</p>
            ) : (
                <Table className="mt-2">
                        <thead>
                            <tr>
                                <Th>Requested ({timeZoneLabel()})</Th>
                                <Th>Window</Th>
                                <Th>Status</Th>
                                <Th className="text-right" />
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((c) => {
                                // videoUrl presence is the watchable signal — PartiallyComplete clips carry
                                // video too, and the server refuses the view redirect when there is none.
                                const done = Boolean(c.videoUrl);
                                return (
                                    <tr key={c.id} className="hover:bg-[var(--surface-2)]">
                                        <Td label="Requested" className="tabular-nums">{formatDateTime(c.requestDate)}</Td>
                                        <Td label="Window" className="tabular-nums text-[var(--muted)]">
                                            {formatTimeOfDay(c.periodStart)} – {formatTimeOfDay(c.periodEnd)}
                                        </Td>
                                        <Td label="Status">
                                            <MediaRequestStatusTag request={c} />
                                        </Td>
                                        <Td className="text-right">
                                            {done && (
                                                <a href={viewUrl(c.id)} target="_blank" rel="noreferrer">
                                                    <Btn tone="quiet">Watch</Btn>
                                                </a>
                                            )}
                                        </Td>
                                    </tr>
                                );
                            })}
                        </tbody>
                </Table>
            )}

            <NewRequestModal
                open={requestOpen}
                onClose={() => setRequestOpen(false)}
                defaultDeviceId={device.id}
                onDone={() => {
                    setRequestOpen(false);
                    clips.reload();
                }}
            />
        </Card>
    );
};
